import React, { useEffect, useState } from 'react';
import { useTranslate } from 'react-admin';
import {
    Box,
    Card,
    CardContent,
    Chip,
    Typography,
    CircularProgress,
    Tooltip,
} from '@mui/material';
import { doc, onSnapshot, Timestamp } from 'firebase/firestore';
import { getFirestore } from 'firebase/firestore';
import { firebaseApp } from '../../providers/firebase';
import { format, formatDistanceToNow } from 'date-fns';

const db = getFirestore(firebaseApp);

interface HealthEntry {
    status: 'up' | 'down';
    statusCode: number | null;
    responseTimeMs: number;
    checkedAt: Timestamp;
    error: string | null;
}

interface ChannelHealth {
    channelName: string;
    streamUrl: string;
    status: 'up' | 'down';
    statusCode: number | null;
    responseTimeMs: number;
    lastCheckedAt: Timestamp;
    error: string | null;
    history: HealthEntry[];
}

interface ChannelHealthDocument {
    lastCheckedAt: Timestamp;
    channels: Record<string, ChannelHealth>;
}

function toDate(ts: Timestamp | null | undefined): Date | null {
    if (!ts) return null;
    try {
        return ts.toDate ? ts.toDate() : new Date(ts as unknown as string);
    } catch {
        return null;
    }
}

function formatTimestamp(ts: Timestamp | null | undefined): string {
    const date = toDate(ts);
    if (!date) return '-';
    return format(date, 'MMM d, yyyy HH:mm:ss');
}

function useChannelHealth() {
    const [data, setData] = useState<ChannelHealthDocument | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const docRef = doc(db, 'sync', 'channelHealth');
        const unsubscribe = onSnapshot(
            docRef,
            (docSnap) => {
                if (docSnap.exists()) {
                    setData(docSnap.data() as ChannelHealthDocument);
                } else {
                    setData(null);
                }
                setLoading(false);
            },
            (error) => {
                console.error('Error fetching channel health:', error);
                setLoading(false);
            }
        );

        return () => unsubscribe();
    }, []);

    return { data, loading };
}

function computeUptime(history: HealthEntry[]): string {
    if (!history || history.length === 0) return '-';
    const upCount = history.filter(e => e.status === 'up').length;
    return ((upCount / history.length) * 100).toFixed(1) + '%';
}

const StatusChip = ({ status }: { status: 'up' | 'down' }) => (
    <Chip
        label={status === 'up' ? 'Online' : 'Offline'}
        color={status === 'up' ? 'success' : 'error'}
        size="small"
    />
);

/** Timeline bar showing history as colored blocks, oldest on left, newest on right. */
const TimelineBar = ({ history }: { history: HealthEntry[] }) => {
    // Reverse so oldest is first (left) and newest is last (right)
    const entries = [...(history || [])].reverse();

    if (entries.length === 0) {
        return (
            <Typography variant="caption" color="textSecondary">
                No history yet
            </Typography>
        );
    }

    return (
        <Box sx={{ display: 'flex', gap: '1px', alignItems: 'center', height: 32, overflowX: 'auto' }}>
            {entries.map((entry, index) => {
                const date = toDate(entry.checkedAt);
                const tooltipText = date
                    ? `${format(date, 'MMM d, HH:mm')} — ${entry.status === 'up' ? 'Online' : 'Offline'} (${entry.responseTimeMs}ms)${entry.error ? ' — ' + entry.error : ''}`
                    : 'Unknown';

                return (
                    <Tooltip key={index} title={tooltipText} arrow placement="top">
                        <Box
                            sx={{
                                width: 4,
                                minWidth: 4,
                                height: '100%',
                                backgroundColor: entry.status === 'up' ? '#4caf50' : '#f44336',
                                borderRadius: index === 0 ? '3px 0 0 3px' : index === entries.length - 1 ? '0 3px 3px 0' : 0,
                                cursor: 'pointer',
                                transition: 'opacity 0.15s',
                                '&:hover': {
                                    opacity: 0.7,
                                },
                            }}
                        />
                    </Tooltip>
                );
            })}
        </Box>
    );
};

/** Time labels below the timeline bar */
const TimelineLabels = ({ history }: { history: HealthEntry[] }) => {
    const entries = [...(history || [])].reverse();
    if (entries.length < 2) return null;

    const oldest = toDate(entries[0]?.checkedAt);
    const newest = toDate(entries[entries.length - 1]?.checkedAt);

    return (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
            <Typography variant="caption" color="textSecondary">
                {oldest ? formatDistanceToNow(oldest, { addSuffix: true }) : ''}
            </Typography>
            <Typography variant="caption" color="textSecondary">
                {newest ? 'now' : ''}
            </Typography>
        </Box>
    );
};

const ChannelHealthPage = () => {
    const translate = useTranslate();
    const { data, loading } = useChannelHealth();

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (!data) {
        return (
            <Box sx={{ padding: '1.5em' }}>
                <Typography variant="h5" gutterBottom>
                    {translate('channelHealthPage.title')}
                </Typography>
                <Typography variant="body1" color="textSecondary">
                    {translate('channelHealthPage.noData')}
                </Typography>
            </Box>
        );
    }

    const channels = data.channels ? Object.entries(data.channels) : [];

    return (
        <Box sx={{ padding: '1.5em' }}>
            <Typography variant="h5" gutterBottom>
                {translate('channelHealthPage.title')}
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
                {translate('channelHealthPage.subtitle')}
            </Typography>

            {/* Channel Health Cards with Timeline */}
            {channels.map(([channelId, channel]) => (
                <Card key={channelId} sx={{ mb: 2 }}>
                    <CardContent>
                        {/* Header row: name, status, response time, uptime */}
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                <Typography variant="h6">
                                    {channel.channelName}
                                </Typography>
                                <StatusChip status={channel.status} />
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                                <Typography variant="body2" color="textSecondary">
                                    {translate('channelHealthPage.responseTime')}: {channel.responseTimeMs}ms
                                </Typography>
                                <Typography variant="body2" color="textSecondary">
                                    {translate('channelHealthPage.uptime')}: {computeUptime(channel.history)}
                                </Typography>
                                <Typography variant="caption" color="textSecondary">
                                    {translate('channelHealthPage.lastChecked')}: {formatTimestamp(channel.lastCheckedAt)}
                                </Typography>
                            </Box>
                        </Box>

                        {/* Timeline bar */}
                        <TimelineBar history={channel.history} />
                        <TimelineLabels history={channel.history} />
                    </CardContent>
                </Card>
            ))}
        </Box>
    );
};

export default ChannelHealthPage;
